// eslint-disable-next-line
import {injectable} from "inversify";
import {TYPE, rootContainer} from '../container/root';
import {IApiResponse, IHttpService} from "../http-service";
import {
    IContactUs,
    ICurrentUserPlan,
    IPlan,
    IPlanEditorEventStory,
    IPlanEditorModel,
    PLAN_TYPES
} from "../../models/Plan";
import {createPagedRequestURLSearchParams} from '../helpers';
import {IFetchListArgs, IPagedData} from '../../stores/abstract-paged-data-store';
import { IMerchantOnboarding } from "../../pages/onboarding/onboarding-types";

interface ISubscriptionPlansFetchListArgs extends IFetchListArgs {
    type?: PLAN_TYPES
}

export interface IPlanResponse {
    redirect_to?: string,
    message?: string
}

interface IPartner {
    id: string,
    name: string,
}

export interface IPlanSetting {
    partners: IPartner[],
}

@injectable()
class SubscriptionPlansService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    async getPlanEditingEvents(planId: number):Promise<IPlanEditorEventStory[]> {
        const res = await this.httpService.get<IApiResponse<IPlanEditorEventStory[]>>(`/rms-api/subscription-plans/editing-events/${planId}`);
        return res.payload;
    }

    async getCurrentUserPlan(): Promise<ICurrentUserPlan> {
        const res = await this.httpService.get<IApiResponse<ICurrentUserPlan>>(`/rms-api/subscription-plans/current`);
        return res.payload;
    }

    async getPlans(): Promise<IApiResponse<IPlan[]>> {
        return await this.httpService.get(`/rms-api/subscription-plans`);
    }

    async getContactUs(): Promise<IContactUs> {
        const res = await this.httpService.get<IApiResponse<IContactUs>>(`/rms-api/subscription-plans/contactus`);
        return res.payload;
    }

    async generateBillingLink(newPlan: IPlan): Promise<IPlanResponse> {
        const res = await this.httpService.post<IApiResponse<IPlanResponse>>('/rms-api/subscription-plans/generate-billing-link', newPlan);
        return res.payload;
    }

    async generateApprovalBillingLink(payload: { plan: IPlan, onboarding: IMerchantOnboarding }): Promise<IPlanResponse> {
        const res = await this.httpService.post<IApiResponse<IPlanResponse>>('/rms-api/subscription-plans/generate-approval-billing-link', payload);
        return res.payload;
    }

    async fetchList(args: ISubscriptionPlansFetchListArgs): Promise<IPagedData<IPlanEditorModel>> {
        const {page, perPage, sortBy, sortDir, type} = args;
        const searchParams = createPagedRequestURLSearchParams(page, perPage, sortBy, sortDir);
        if (type) {
            searchParams.append('type', type);
        }
        const res = await this.httpService.get<IApiResponse<IPagedData<IPlanEditorModel>>>
            (`/rms-api/subscription-plans/list?${searchParams.toString()}`);

        return res.payload;
    }

    async fetchSettings(): Promise<IPlanSetting> {
        const res = await this.httpService.get<IApiResponse<IPlanSetting>>(`/rms-api/subscription-plans/settings`);
        return res.payload;
    }

    async fetchPlan(id: number): Promise<IPlanEditorModel> {
        const res = await this.httpService.get<IApiResponse<IPlanEditorModel>>(`/rms-api/subscription-plans/${id}`);
        return res.payload;
    }

    async create(data: IPlanEditorModel): Promise<IPlanEditorModel> {
        const res = await this.httpService.post<IApiResponse<IPlanEditorModel>>('/rms-api/subscription-plans', data);
        return res.payload;
    }

    async update(id: number, data: IPlanEditorModel): Promise<IPlanEditorModel> {

        const res = await this.httpService.put<IApiResponse<IPlanEditorModel>>(`/rms-api/subscription-plans/${id}`, data);
        return res.payload;
    }

    private planLogName(id: number): string {
        return `subscription-plan-${id}`;
    }

    public markPlanAsNewlyCreated(id: number) {
        sessionStorage.setItem(this.planLogName(id), 'new');
    }

    public isNewlyCreatedPlan(id: number) {
        return !!sessionStorage.getItem(this.planLogName(id));
    }

    async getOnBoardingPlans(): Promise<IPlan[]> {
        const res = await this.httpService.get<IApiResponse<IPlan[]>>(`/rms-api/on-boarding-plans`);
        return res.payload;
    }

}

export default SubscriptionPlansService;
